/**
 * BasketReviewDialog — interactive credit/debit-strategy BUILDER for the chart.
 *
 * Opens for a chosen strategy (e.g. Short Straddle), previews the default ATM
 * legs, and lets the user adjust each leg's strike (+/- steppers) and the lots —
 * recomputing the payoff curve + full economics live (no order placed) via
 * POST /options/baskets/preview. Approve persists the chosen legs
 * (/options/baskets/from-legs) and executes; the monitor then manages it.
 *
 * Phase A payoff (PayoffChartPro) + B1-part-2 selectable strikes.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Chip, CircularProgress, IconButton, Paper, PaperProps, FormControlLabel, Checkbox, InputBase, Tooltip,
  Select, MenuItem,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Draggable from 'react-draggable';
import toast from 'react-hot-toast';
import PayoffChartPro from './PayoffChartPro';
import { Term, RiskMeter, PlainStatus, useAccountCapital } from '../options/shared/OptionsUi';
import { optionsService } from '../../services/options.service';
import { syntheticSymbolsService } from '../../services/syntheticSymbols.service';
import { useAppTheme } from '../../hooks/useAppTheme';
import { expiryClock } from '../../utils/optionExpiry';
import { AUTO_BUILD_STRATEGIES } from '../../utils/optionStrategyLegs';

interface Leg { action: 'BUY' | 'SELL'; optionType: 'CE' | 'PE'; strike: number; expiry: string; }

/** Draggable Paper — grabs by the title bar (#basket-builder-handle); buttons/icons opt out via `cancel`. */
const DraggablePaper: React.FC<PaperProps> = (props) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
      handle="#basket-builder-handle"
      cancel={'[class*="MuiIconButton-root"],[class*="MuiButtonBase-root"]'}
      bounds="parent"
    >
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
};

interface Props {
  open: boolean;
  underlyingSymbol: string;     // base, e.g. 'BTC'
  strategyId: string | null;
  strategyLabel: string;
  spotPrice: number;
  /** Legs picked on the chart (click-to-build). When provided, the initial
   *  preview is seeded from these instead of the strategy's default ATM legs. */
  initialLegs?: Leg[] | null;
  /** Underlying's option sizing fields (Instruments page) — used to auto-size the
   *  default lots: credit strategies use OPT Sell, debit use OPT Buy, both × Leverage. */
  sellInvestmentInr?: number;
  buyInvestmentInr?: number;
  leverage?: number;
  onClose: () => void;
  onExecuted?: () => void;
  onCardChange?: (card: any | null) => void;  // publishes the live card (for the underlying payoff-zones overlay)
}

const fmtInr = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;

/** Short date for a chip, e.g. "27 Jun". */
const mmmdd = (iso: string) => {
  const [y, mo, d] = String(iso).split('T')[0].split('-').map(Number);
  if (!y || !mo || !d) return iso;
  return new Date(Date.UTC(y, mo - 1, d)).toLocaleString('en-IN', { timeZone: 'UTC', day: '2-digit', month: 'short' });
};

/** Protective-wing expiry choices RELATIVE to the short legs' expiry: same, the next
 *  ≤2 daily expiries, the next weekly, and the next monthly — only those that exist in
 *  the chain. Lets the user roll the long hedge out to cut its theta bleed. */
type ProtOpt = { label: string; iso: string; tag: 'near' | 'daily' | 'weekly' | 'monthly'; dte: number };
const protectionOptionsFor = (avail: string[], shortIso: string): ProtOpt[] => {
  if (!shortIso) return [];
  const dayMs = 86_400_000;
  const D = (e: string) => Date.UTC(+e.slice(0, 4), +e.slice(5, 7) - 1, +e.slice(8, 10), 12, 0, 0);
  const sMs = D(shortIso);
  const dteOf = (e: string) => Math.round((D(e) - sMs) / dayMs);
  const opts: ProtOpt[] = [{ label: 'Same expiry', iso: shortIso, tag: 'near', dte: 0 }];
  const futures = Array.from(new Set((avail || []).filter(e => D(e) > sMs))).sort((a, b) => D(a) - D(b));
  // Up to 2 daily — dense (≤2.5d apart) from the short / previous daily.
  const used = new Set<string>([shortIso]);
  let prev = shortIso;
  for (const e of futures) {
    if ((D(e) - D(prev)) / dayMs <= 2.5) { opts.push({ label: `Daily +${dteOf(e)}d`, iso: e, tag: 'daily', dte: dteOf(e) }); used.add(e); prev = e; if (opts.filter(o => o.tag === 'daily').length >= 2) break; }
    else break;
  }
  // Next weekly — first future 3–12d out not already taken.
  const weekly = futures.find(e => !used.has(e) && dteOf(e) >= 3 && dteOf(e) <= 12);
  if (weekly) { opts.push({ label: `Weekly +${dteOf(weekly)}d`, iso: weekly, tag: 'weekly', dte: dteOf(weekly) }); used.add(weekly); }
  // Next monthly — a later-month expiry that's the last available of its month (or ≥20d out).
  const monthly = futures.find(e => !used.has(e) && dteOf(e) >= 20 && !futures.some(f => D(f) > D(e) && f.slice(0, 7) === e.slice(0, 7)));
  if (monthly) opts.push({ label: `Monthly +${dteOf(monthly)}d`, iso: monthly, tag: 'monthly', dte: dteOf(monthly) });
  return opts;
};

/** Contiguous spot ranges where P&L-at-expiry ≥ 0 (the operative profitable zone). */
const profitZones = (pd: any): Array<[number, number]> => {
  const xs: number[] = pd?.spotRange || [], ys: number[] = pd?.expiry || [];
  if (!xs.length || !ys.length) return [];
  const zones: Array<[number, number]> = [];
  let start: number | null = null;
  for (let i = 0; i < xs.length; i++) {
    const ok = Number(ys[i]) >= 0;
    if (ok && start === null) start = xs[i];
    else if (!ok && start !== null) { zones.push([start, xs[i]]); start = null; }
  }
  if (start !== null) zones.push([start, xs[xs.length - 1]]);
  return zones;
};

const BasketReviewDialog: React.FC<Props> = ({ open, underlyingSymbol, strategyId, strategyLabel, spotPrice, initialLegs, sellInvestmentInr, buyInvestmentInr, leverage, onClose, onExecuted, onCardChange }) => {
  const { C } = useAppTheme();
  const capital = useAccountCapital();
  const [card, setCard] = useState<any | null>(null);
  const [chain, setChain] = useState<{ atmStrike: number; strikeStep: number; strikes: number[]; expiries: string[]; spot: number; availableExpiries?: string[] } | null>(null);
  const [legs, setLegs] = useState<Leg[]>([]);
  const [lots, setLots] = useState(1);
  // Auto-close levels: book at tpPct% of MAX PROFIT, stop at slPct% of MAX LOSS
  // (defined risk) — same semantics as the backend computeBasketTpSl and the
  // running-basket editor (OptionBasketsMonitor). Defaults mirror the backend (50/50).
  const [tpPct, setTpPct] = useState(50);
  const [slPct, setSlPct] = useState(50);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmLive, setConfirmLive] = useState(false);
  const [spreadAck, setSpreadAck] = useState(false);
  // Worst per-leg bid/ask spread % (native units cancel) — surfaced as a
  // non-blocking warning the user must acknowledge before placing LIVE.
  const worstSpreadPct = useMemo(() => {
    const ls: any[] = (card?.legs as any[]) || [];
    let w = 0;
    for (const l of ls) { const s = Number(l.bidAskSpread) / Math.max(0.01, Number(l.ltp)); if (Number.isFinite(s)) w = Math.max(w, s); }
    return w;
  }, [card]);
  const spreadWarn = worstSpreadPct > 0.02;
  // A SELL leg sitting within ~0.5% of spot is effectively at-the-money — it will be
  // flagged for adjustment (high gamma) the moment it's placed, so warn at build time.
  const atmShort = useMemo(() => {
    if (!spotPrice || !legs.length) return null;
    let nearest: { leg: Leg; d: number } | null = null;
    for (const l of legs) {
      if (l.action !== 'SELL') continue;
      const d = Math.abs(l.strike - spotPrice) / spotPrice;
      if (nearest == null || d < nearest.d) nearest = { leg: l, d };
    }
    return nearest && nearest.d <= 0.005 ? nearest.leg : null;
  }, [legs, spotPrice]);
  const [error, setError] = useState<string | null>(null);
  const reqRef = useRef(0);
  // Auto-size lots ONCE per open (so user adjustments aren't overwritten by recompute).
  const autoSizedRef = useRef(false);
  const [autoLotsNote, setAutoLotsNote] = useState<string | null>(null);
  // Credit / defined-risk baskets (a SELL short + a BUY protective wing) can roll the
  // long wings to a LATER expiry so the hedge bleeds less theta than the near shorts.
  const supportsProtection = useMemo(() => {
    const isCreditShape = card?.riskShape === 'credit';
    return isCreditShape && legs.some(l => l.action === 'BUY') && legs.some(l => l.action === 'SELL');
  }, [card, legs]);

  /** Lots affordable from the underlying's option budget × leverage, using the
   *  card's per-lot margin (INR). Credit strategies (netPremium < 0) use OPT Sell;
   *  debit (netPremium ≥ 0) use OPT Buy. marginEstimate already reflects per-lot
   *  capital for both (credit = defined-risk margin; debit = net debit paid). */
  const autoSizeLots = (c: any): { lots: number; note: string | null } => {
    const isCredit = Number(c?.netPremium) < 0;
    const budget = isCredit ? Number(sellInvestmentInr || 0) : Number(buyInvestmentInr || 0);
    const lev = leverage && leverage > 0 ? leverage : 1;
    const perLot = Number(c?.marginEstimate) || 0;
    if (!(budget > 0) || !(perLot > 0)) return { lots: 1, note: null };
    const lots = Math.max(1, Math.floor((budget * lev) / perLot));
    return { lots, note: `auto-sized from OPT ${isCredit ? 'Sell' : 'Buy'} ₹${Math.round(budget).toLocaleString('en-IN')} × ${lev}× lev` };
  };

  // Initial open → preview the default ATM legs.
  useEffect(() => {
    if (!open || !strategyId || !underlyingSymbol) { setCard(null); setChain(null); setLegs([]); setLots(1); setError(null); autoSizedRef.current = false; setAutoLotsNote(null); return; }
    let cancelled = false;
    const id = ++reqRef.current;
    setLoading(true); setError(null);
    (async () => {
      try {
        // Seed from chart-picked legs when present; else the strategy's default ATM legs.
        const seedLegs = initialLegs && initialLegs.length ? initialLegs : undefined;
        const r: any = await optionsService.previewBasket(seedLegs
          ? { underlyingSymbol, strategyId, legs: seedLegs }
          : { underlyingSymbol, strategyId });
        if (cancelled || reqRef.current !== id) return;
        const d = r?.data?.data;
        if (d?.card) {
          setCard(d.card); setChain(d.chain);
          setLegs((d.card.legs || []).map((l: any) => ({ action: l.action, optionType: l.optionType, strike: Number(l.strike), expiry: l.expiry })).sort((a: any, b: any) => b.strike - a.strike));
          // Auto-size the default lots from the underlying's budget + leverage (once).
          if (!autoSizedRef.current) {
            const { lots: auto, note } = autoSizeLots(d.card);
            setLots(auto); setAutoLotsNote(note);
            autoSizedRef.current = true;
          }
        } else { setError('Could not build this strategy on the current chain.'); }
      } catch (e: any) { if (!cancelled) setError(e?.response?.data?.error?.message || e?.message || 'Preview failed'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, strategyId, underlyingSymbol, initialLegs]);

  // Recompute when legs change (debounced).
  const recompute = (nextLegs: Leg[]) => {
    if (!strategyId) return;
    const id = ++reqRef.current;
    setLoading(true);
    setTimeout(async () => {
      if (reqRef.current !== id) return;
      try {
        const r: any = await optionsService.previewBasket({ underlyingSymbol, strategyId, legs: nextLegs });
        if (reqRef.current !== id) return;
        const d = r?.data?.data;
        if (d?.card) {
          setCard(d.card); if (d.chain) setChain(d.chain); setError(null);
          // Sync the displayed legs to what the server actually built — strikes are
          // unchanged, but a leg's expiry may have been moved to the nearest day that
          // lists its strike (so the leg rows + the approve payload stay accurate).
          setLegs((d.card.legs || []).map((l: any) => ({ action: l.action, optionType: l.optionType, strike: Number(l.strike), expiry: l.expiry })).sort((a: any, b: any) => b.strike - a.strike));
        }
        else setError('Invalid strikes for the current chain.');
      } catch (e: any) { setError(e?.response?.data?.error?.message || 'Recompute failed'); }
      finally { if (reqRef.current === id) setLoading(false); }
    }, 250);
  };

  // Identical legs (same side/type/strike/expiry) are collapsed into ONE row with a
  // ×N ratio control — so a 1×2 ratio back-spread shows a single "BUY PE ×2" row whose
  // strike stepper moves BOTH longs together (moving them closer to ATM narrows the
  // mid-range loss valley in one click), and whose ×N changes the ratio.
  type LegGroup = { action: 'BUY' | 'SELL'; optionType: 'CE' | 'PE'; strike: number; expiry: string; count: number };
  const legGroups = useMemo<LegGroup[]>(() => {
    const m = new Map<string, LegGroup>();
    for (const l of legs) {
      const k = `${l.action}|${l.optionType}|${l.strike}|${l.expiry}`;
      const g = m.get(k);
      if (g) g.count++; else m.set(k, { action: l.action, optionType: l.optionType, strike: l.strike, expiry: l.expiry, count: 1 });
    }
    return Array.from(m.values()).sort((a, b) => b.strike - a.strike);
  }, [legs]);

  const sameGroup = (l: Leg, g: LegGroup) => l.action === g.action && l.optionType === g.optionType && l.strike === g.strike && l.expiry === g.expiry;

  // Move every leg in a group by one strike step (keeps the group together).
  const stepGroupStrike = (g: LegGroup, dir: 1 | -1) => {
    if (!chain) return;
    const next = legs.map(l => sameGroup(l, g) ? { ...l, strike: l.strike + dir * chain.strikeStep } : l);
    setLegs(next);
    recompute(next);
  };
  // Change a group's quantity (ratio) — add/remove duplicate legs (1..10 per leg).
  const setGroupCount = (g: LegGroup, n: number) => {
    const count = Math.max(1, Math.min(10, Math.floor(n)));
    if (count === g.count) return;
    const others = legs.filter(l => !sameGroup(l, g));
    const dup: Leg = { action: g.action, optionType: g.optionType, strike: g.strike, expiry: g.expiry };
    const next = [...others, ...Array.from({ length: count }, () => ({ ...dup }))].sort((a, b) => b.strike - a.strike);
    setLegs(next);
    recompute(next);
  };
  const isRatio = (strategyId || '').includes('backspread');

  // Near expiry = the short (SELL) legs' expiry. The protective BUY legs may sit at
  // the same or a LATER expiry; we derive the current pick from the BUY legs.
  const nearExpiry = useMemo(() => legs.find(l => l.action === 'SELL')?.expiry || legs[0]?.expiry || '', [legs]);
  const buyExpiry = useMemo(() => legs.find(l => l.action === 'BUY')?.expiry || nearExpiry, [legs, nearExpiry]);
  const protOptions = useMemo(() => protectionOptionsFor(chain?.availableExpiries || [], nearExpiry), [chain, nearExpiry]);

  // Roll the protective BUY wings to the chosen expiry (SELL shorts stay near). The
  // server re-prices each leg at its OWN expiry → all stats refresh on recompute.
  const setProtectionExpiry = (targetIso: string) => {
    if (!targetIso) return;
    const next = legs.map(l => l.action === 'BUY' ? { ...l, expiry: targetIso } : l);
    setLegs(next);
    recompute(next);
  };

  // All tradable expiries for the underlying (daily / weekly / monthly), for the
  // Build expiry selector — labelled by date + days-to-go so you can pick e.g. a
  // weekly back-spread. Sorted soonest-first.
  const expiryOptions = useMemo(() => {
    const list = (chain?.availableExpiries && chain.availableExpiries.length ? chain.availableExpiries : (chain?.expiries || []))
      .map(e => String(e).slice(0, 10)).filter(Boolean);
    const uniq = Array.from(new Set(list)).sort();
    const dayMs = 86_400_000;
    const now = Date.now();
    return uniq.map(iso => {
      const t = Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10), 12, 0, 0);
      const dte = Math.max(0, Math.round((t - now) / dayMs));
      return { iso, label: `${mmmdd(iso)} · ${dte}d`, dte };
    });
  }, [chain]);

  // Change the PRIMARY (short-leg) expiry. Auto-built strategies (ratio back-spreads)
  // re-run their premium-matched build on the new expiry; click-built strategies keep
  // their strikes and re-price all legs on the new expiry (protection roll resets).
  const applyExpiry = (exp: string) => {
    if (!exp || exp === nearExpiry) return;
    if (AUTO_BUILD_STRATEGIES.has(strategyId || '')) {
      const id = ++reqRef.current; setLoading(true);
      (async () => {
        try {
          const r: any = await optionsService.previewBasket({ underlyingSymbol, strategyId: strategyId!, expiry: exp });
          if (reqRef.current !== id) return;
          const d = r?.data?.data;
          if (d?.card) {
            setCard(d.card); if (d.chain) setChain(d.chain); setError(null);
            setLegs((d.card.legs || []).map((l: any) => ({ action: l.action, optionType: l.optionType, strike: Number(l.strike), expiry: l.expiry })).sort((a: any, b: any) => b.strike - a.strike));
          } else setError('Could not build this strategy on that expiry.');
        } catch (e: any) { setError(e?.response?.data?.error?.message || 'Could not switch expiry'); }
        finally { if (reqRef.current === id) setLoading(false); }
      })();
    } else {
      const next = legs.map(l => ({ ...l, expiry: exp }));
      setLegs(next);
      recompute(next);
    }
  };

  // Display economics scaled by lots (preview is 1-lot).
  const disp = useMemo(() => {
    if (!card) return null;
    const m = lots;
    return {
      ...card,
      maxProfit: Number(card.maxProfit || 0) * m,
      maxLoss: Number(card.maxLoss || 0) * m,
      marginEstimate: Number(card.marginEstimate || 0) * m,
      netPremium: Number(card.netPremium || 0) * m,
      payoffData: card.payoffData ? {
        ...card.payoffData,
        expiry: (card.payoffData.expiry || []).map((v: number) => v * m),
        today: (card.payoffData.today || []).map((v: number) => v * m),
        ...(card.payoffData.far ? { far: card.payoffData.far.map((v: number) => v * m) } : {}),
      } : card.payoffData,
    };
  }, [card, lots]);

  // Publish the live (lots-scaled) card to the parent for the underlying overlay.
  useEffect(() => { onCardChange?.(open ? disp : null); }, [disp, open, onCardChange]);

  // Place the basket directly: PAPER = simulated fills (no broker order); LIVE =
  // real exchange orders via the gates (buy legs first). Both are then monitored.
  const place = async (mode: 'PAPER' | 'LIVE') => {
    if (!strategyId || !legs.length) return;
    setBusy(true);
    try {
      const r: any = await optionsService.createBasketFromLegs({ underlyingSymbol, strategyId, legs, lots, mode, tpPct, slPct });
      const basketId = r?.data?.data?.basketId;
      if (!basketId) throw new Error('Basket not created');
      await optionsService.approveBasket(basketId);
      setConfirmLive(false);
      toast.success(mode === 'PAPER'
        ? `📝 Paper basket placed — simulated; track it under Trades → Option baskets (TP ${tpPct}% of max profit / SL ${slPct}% of max loss).`
        : `🟢 LIVE basket placed — the monitor manages it (TP ${tpPct}% of max profit / SL ${slPct}% of max loss); track it under Trades → Option baskets.`, { duration: 8000, id: 'basket-approve' });
      onExecuted?.(); onClose();
    } catch (e: any) {
      const gs = e?.response?.data?.error?.gates;
      toast.error(gs?.length ? `Blocked: ${gs.map((g: any) => g.message).join('; ')}` : (e?.response?.data?.error?.message || e?.message || 'Place failed'), { duration: 9000, id: 'basket-approve' });
    } finally { setBusy(false); }
  };

  const econ = disp && {
    isCredit: Number(disp.netPremium) < 0,
    maxLoss: Math.abs(Number(disp.maxLoss || 0)),
    rr: Math.abs(Number(disp.maxLoss)) > 0 && Number(disp.maxProfit) > 0 ? Number(disp.maxProfit) / Math.abs(Number(disp.maxLoss)) : null,
  };

  // Expiry-by (the near/short leg settles first) + time-to-go.
  const expClock = useMemo(() => expiryClock(nearExpiry, card?.feedSource), [nearExpiry, card]);

  // Operative profitable range(s) + a plain-language "how/when profit materialises" note.
  const profitInfo = useMemo(() => {
    const pd = disp?.payoffData;
    if (!pd?.spotRange?.length) return null;
    const xs: number[] = pd.spotRange, dMin = xs[0], dMax = xs[xs.length - 1];
    const zones = profitZones(pd);
    const fmt = (v: number) => Math.round(v).toLocaleString('en-IN');
    const range = !zones.length
      ? 'no profitable zone on this chain'
      : zones.map(([lo, hi]) => {
          const tLo = lo <= dMin + 1e-6, tHi = hi >= dMax - 1e-6;
          if (tLo && tHi) return 'any level';
          if (tLo) return `below ${fmt(hi)}`;
          if (tHi) return `above ${fmt(lo)}`;
          return `${fmt(lo)}–${fmt(hi)}`;
        }).join(' or ');
    const theta = Number(disp?.greeks?.theta || 0);
    const when = card?.feedSource && expClock ? expClock.label : (nearExpiry || 'expiry');
    const collectsDecay = theta >= 0 || econ?.isCredit;
    const note = collectsDecay
      ? `Time decay works for you — max profit accrues as theta erodes the options; realised if ${underlyingSymbol} is in the range at expiry (${expClock?.dte ? `~${expClock.dte} to go` : when}).`
      : `Directional — needs the move to play out before ${when}; time decay works against you while you wait.`;
    return { zones, range, note };
  }, [disp, card, expClock, econ, nearExpiry, underlyingSymbol]);
  const Metric: React.FC<{ label: React.ReactNode; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 86 }}>
      <Typography component="div" sx={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: 0.3, color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: color || 'text.primary', lineHeight: 1.3 }}>{value}</Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      maxWidth="sm"
      fullWidth
      hideBackdrop
      disableEnforceFocus
      disableScrollLock
      PaperComponent={DraggablePaper}
      PaperProps={{ sx: { borderRadius: 2, pointerEvents: 'auto', boxShadow: 8, border: `1px solid ${C.chartGrid}` } }}
      // Non-modal: let pointer events fall through to the underlying chart except
      // on the panel itself, so the user can pan/zoom the chart while the builder
      // floats and updates the payoff zones live.
      sx={{
        '& .MuiDialog-container': { pointerEvents: 'none', alignItems: 'flex-start', justifyContent: 'flex-end' },
        '& .MuiDialog-paper': { mt: 9, mr: 3, maxHeight: 'calc(100vh - 96px)' },
      }}
    >
      <DialogTitle id="basket-builder-handle" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.2, cursor: 'move' }}>
        <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.disabled', mr: -0.5 }} />
        <Typography sx={{ fontSize: '1rem', fontWeight: 800 }}>Build — {strategyLabel} · {underlyingSymbol}</Typography>
        {econ && (
          <Chip size="small" label={econ.isCredit ? `Credit ${fmtInr(Math.abs(disp.netPremium))}` : `Debit ${fmtInr(disp.netPremium)}`}
            sx={{ height: 20, fontWeight: 700, fontSize: '0.66rem', bgcolor: econ.isCredit ? C.bullMuted : C.warnMuted, color: econ.isCredit ? C.bull : C.warn }} />
        )}
        {loading && <CircularProgress size={15} sx={{ ml: 0.5 }} />}
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.5 }}>
        {error && !disp ? (
          <Typography variant="body2" sx={{ color: C.bear, py: 4, textAlign: 'center' }}>{error}</Typography>
        ) : !disp ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1.5 }}>
            <CircularProgress size={22} /> <Typography variant="body2" color="text.secondary">Loading payoff & risk…</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ bgcolor: 'rgba(127,127,127,0.06)', borderRadius: 1.5, p: 1 }}>
              <PayoffChartPro basket={disp} spotPrice={spotPrice} profitBand={profitInfo?.zones} underlyingSymbol={underlyingSymbol} />
            </Box>

            {/* Expiry-by + operative profitable range + how/when profit materialises. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, bgcolor: econ!.isCredit ? C.bullMuted : 'rgba(127,127,127,0.06)', border: `1px solid ${econ!.isCredit ? C.bull : C.chartGrid}`, borderRadius: 1.5, p: 1 }}>
              {expClock && (
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>Expires</Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 800 }}>{expClock.label}</Typography>
                  <Chip size="small" label={expClock.expired ? 'expired' : `${expClock.dte} left`} sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: C.warnMuted, color: C.warn }} />
                </Box>
              )}
              {/* Plain-English headline — the one sentence to read before placing. */}
              {econ && (
                <PlainStatus tone={econ.maxLoss === 0 ? 'warn' : 'good'} emoji={econ.maxLoss === 0 ? '⚠️' : '✅'}>
                  {profitInfo?.range && profitInfo.range !== 'no profitable zone on this chain' ? `You profit if ${underlyingSymbol} finishes ${profitInfo.range}. ` : ''}
                  Risk {econ.maxLoss === 0 ? 'an undefined amount' : `up to ${fmtInr(econ.maxLoss)}`} to make up to {fmtInr(disp.maxProfit)}
                  {/* The implied-distribution figure, never the old range fraction. A sentence a
                      beginner reads before placing is the LAST place to show a number that calls a
                      long strangle a 96% chance. Silent when it cannot be computed. */}
                  {(disp as any).probabilityOfProfit != null
                    ? ` · about ${Math.round((disp as any).probabilityOfProfit)}% chance of profit under the market's own implied distribution.`
                    : '.'}
                </PlainStatus>
              )}
              {/* Beginner note for a net-DEBIT structure — you pay up front, loss is capped,
                  and the auto-stop won't fire for the first few minutes (entry-spread guard). */}
              {econ && !econ.isCredit && (
                <PlainStatus tone="neutral" emoji="ℹ️">
                  You <strong>pay {fmtInr(Math.abs(disp.netPremium))} up front</strong> for this (a debit).
                  {econ.maxLoss > 0 ? <> Your loss is <strong>capped at {fmtInr(econ.maxLoss)}</strong> — you can't lose more than that.</> : null}
                  {' '}The auto stop-loss stays off for the first few minutes after placing, so the entry bid/ask spread can't square it off instantly.
                </PlainStatus>
              )}
              {profitInfo && (
                <Typography sx={{ fontSize: '0.78rem' }}>
                  <Box component="span" sx={{ fontWeight: 800, color: C.bull }}>Profitable range:</Box>{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>{underlyingSymbol} {profitInfo.range}</Box>
                  {(disp.breakEvens || []).length > 0 && (
                    <Box component="span" sx={{ color: 'text.secondary' }}> · break-even {disp.breakEvens.map((b: number) => Math.round(b).toLocaleString('en-IN')).join(' / ')}</Box>
                  )}
                </Typography>
              )}
              {profitInfo && (
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.45 }}>{profitInfo.note}</Typography>
              )}
            </Box>

            {/* Auto-corrections (e.g. a strike not listed on the chosen day → moved
                to the nearest day that lists it). */}
            {Array.isArray(disp.adjustments) && disp.adjustments.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start', bgcolor: C.warnMuted, border: `1px solid ${C.warn}`, borderRadius: 1, p: 0.9 }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: C.warn, mt: '1px' }} />
                <Box>
                  {disp.adjustments.map((a: string, i: number) => (
                    <Typography key={i} sx={{ fontSize: '0.7rem', color: C.warn, fontWeight: 600, lineHeight: 1.45 }}>{a}</Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* At-the-money short warning — placing this will trigger an adjustment alert. */}
            {atmShort && (
              <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start', bgcolor: C.warnMuted, border: `1px solid ${C.warn}`, borderRadius: 1, p: 0.9 }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: C.warn, mt: '1px' }} />
                <Typography sx={{ fontSize: '0.7rem', color: C.warn, fontWeight: 600, lineHeight: 1.45 }}>
                  Short {atmShort.optionType} {atmShort.strike.toLocaleString('en-IN')} is at-the-money (spot ≈ {Math.round(spotPrice).toLocaleString('en-IN')}). It will be flagged for adjustment immediately (high gamma near expiry) — step this short further OTM to widen the safe zone.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, px: 0.5 }}>
              <Metric label={<Term k="maxProfit">Max profit</Term>} value={fmtInr(disp.maxProfit)} color={C.bull} />
              <Metric label={<Term k="maxLoss">Max loss</Term>} value={econ!.maxLoss === 0 ? 'Undefined' : fmtInr(econ!.maxLoss)} color={C.bear} />
              <Metric label={<Term k={econ!.isCredit ? 'credit' : 'debit'}>{econ!.isCredit ? 'Net credit' : 'Net debit'}</Term>} value={fmtInr(Math.abs(disp.netPremium))} color={econ!.isCredit ? C.bull : C.warn} />
              {/* Margin source (item 14c): broker SPAN vs in-app heuristic — a surprise
                  at the broker should be visible BEFORE placing. */}
              <Metric label={<Term k="margin">Margin required</Term>}
                value={`${fmtInr(disp.marginEstimate)} ${card?.marginSource === 'BROKER' ? '· Zerodha SPAN' : '· estimate'}`} />
              <Metric label={<Term k="rewardRisk">R : R</Term>} value={econ!.rr != null ? `${econ!.rr.toFixed(2)} : 1` : '—'} />
              <Metric label={<Term k="winProbability">Chance of profit</Term>}
                value={(disp as any).probabilityOfProfit != null ? `${Math.round((disp as any).probabilityOfProfit)}%` : '—'} />
              <Metric label="ROI" value={disp.roi != null ? `${Math.round(disp.roi)}%` : '—'} />
            </Box>
            {/* Risk in context — ₹ + % of your total capital. */}
            {econ && econ.maxLoss > 0 && (
              <Box sx={{ px: 0.5 }}>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Most you can lose</Typography>
                <RiskMeter riskInr={econ.maxLoss} capitalInr={capital ?? undefined} compact />
              </Box>
            )}

            {/* Legs with strike +/- steppers */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: 0.4, color: 'text.secondary', textTransform: 'uppercase' }}>Legs — adjust strikes, ratio &amp; lots</Typography>
                {/* Expiry selector — pick the daily/weekly/monthly expiry to build on. */}
                {expiryOptions.length > 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, ml: 1 }}>
                    <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', fontWeight: 700 }}>Expiry</Typography>
                    <Select size="small" variant="standard" value={nearExpiry || ''} disabled={loading}
                      onChange={e => applyExpiry(String(e.target.value))}
                      sx={{ fontSize: '0.72rem', fontWeight: 700, minWidth: 92 }}>
                      {expiryOptions.map(o => <MenuItem key={o.iso} value={o.iso} sx={{ fontSize: '0.74rem' }}>{o.label}</MenuItem>)}
                    </Select>
                  </Box>
                )}
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>Lots</Typography>
                  <IconButton size="small" onClick={() => setLots(l => Math.max(1, l - 1))} sx={{ p: 0.2 }}>−</IconButton>
                  <InputBase
                    value={lots}
                    onChange={e => { const n = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10); setLots(Number.isFinite(n) && n > 0 ? n : 1); }}
                    onFocus={e => e.target.select()}
                    inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', padding: 0, fontSize: '0.85rem', fontWeight: 800 } }}
                    sx={{ width: 44, '& input': { MozAppearance: 'textfield' } }}
                  />
                  <IconButton size="small" onClick={() => setLots(l => l + 1)} sx={{ p: 0.2 }}>+</IconButton>
                </Box>
              </Box>
              {autoLotsNote && (
                <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', mt: -0.3, mb: 0.3, textAlign: 'right' }}>{autoLotsNote}</Typography>
              )}
              {/* Auto-close levels (premium-based) — the monitor books profit at TP% of
                  the premium captured and stops at SL% adverse premium move. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.6, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: 'text.secondary' }}>Auto-close:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <Typography sx={{ fontSize: '0.64rem', color: '#2e7d32', fontWeight: 700 }}>Take profit</Typography>
                  <InputBase value={tpPct}
                    onChange={e => { const n = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10); setTpPct(Number.isFinite(n) ? Math.max(1, Math.min(99, n)) : 50); }}
                    onFocus={e => e.target.select()}
                    inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', padding: 0, fontSize: '0.8rem', fontWeight: 800 } }}
                    sx={{ width: 34 }} />
                  <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>%</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <Typography sx={{ fontSize: '0.64rem', color: '#c62828', fontWeight: 700 }}>Stop loss</Typography>
                  <InputBase value={slPct}
                    onChange={e => { const n = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10); setSlPct(Number.isFinite(n) ? Math.max(5, Math.min(500, n)) : 50); }}
                    onFocus={e => e.target.select()}
                    inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', padding: 0, fontSize: '0.8rem', fontWeight: 800 } }}
                    sx={{ width: 40 }} />
                  <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>%</Typography>
                </Box>
                <Tooltip arrow title="The monitor closes the basket when the profit reaches TP% of the MAX PROFIT, or when the loss reaches SL% of the MAX LOSS (the defined risk). For credit structures the stop never sits tighter than 2× the credit captured. You can change these later on the running basket too.">
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
              </Box>
              {/* Protective wings expiry — credit / defined-risk baskets. Rolling the
                  BUY wings to a later expiry makes the hedge decay slower (less theta
                  drag). Choices are relative to the short legs' expiry: same, next ≤2
                  daily, next weekly, next monthly — whichever the chain lists. */}
              {supportsProtection && protOptions.length > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.6, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: 'text.secondary', width: '100%' }}>
                    Protection wings (BUY legs) expiry — roll out to cut theta bleed:
                  </Typography>
                  {protOptions.map(o => {
                    const sel = o.iso === buyExpiry;
                    return (
                      <Chip key={o.iso} size="small"
                        label={o.tag === 'near' ? 'Same expiry' : `${o.label} · ${mmmdd(o.iso)}`}
                        onClick={() => setProtectionExpiry(o.iso)}
                        variant={sel ? 'filled' : 'outlined'}
                        sx={{ height: 22, fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer',
                          bgcolor: sel ? (o.tag === 'near' ? C.bullMuted : C.warnMuted) : 'transparent',
                          color: sel ? (o.tag === 'near' ? C.bull : C.warn) : 'text.secondary' }} />
                    );
                  })}
                  {buyExpiry !== nearExpiry && (
                    <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary', width: '100%' }}>solid = P&L at the near (short) expiry · dashed = at the far (wing) expiry</Typography>
                  )}
                </Box>
              )}
              {isRatio && (
                <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', mb: 0.4, lineHeight: 1.4 }}>
                  💡 Move the <strong>BUY</strong> strikes <strong>up (closer to spot)</strong> to shrink the mid-range loss zone — it costs a little more premium. The <strong>×N</strong> control changes the ratio (e.g. 1×2 → 1×3).
                </Typography>
              )}
              {legGroups.map((g, gi) => (
                <Box key={gi} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35, flexWrap: 'wrap' }}>
                  <Chip size="small" label={g.action} sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: g.action === 'SELL' ? C.bearMuted : C.bullMuted, color: g.action === 'SELL' ? C.bear : C.bull }} />
                  <span style={{ fontWeight: 700, minWidth: 28 }}>{g.optionType}</span>
                  <IconButton size="small" onClick={() => stepGroupStrike(g, -1)} disabled={loading} sx={{ p: 0.2, border: `1px solid ${C.chartGrid}` }}>−</IconButton>
                  <span style={{ fontWeight: 800, minWidth: 64, textAlign: 'center' }}>{g.strike.toLocaleString('en-IN')}</span>
                  <IconButton size="small" onClick={() => stepGroupStrike(g, 1)} disabled={loading} sx={{ p: 0.2, border: `1px solid ${C.chartGrid}` }}>+</IconButton>
                  {/* ×N ratio control */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, ml: 0.5 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 700 }}>×</Typography>
                    <IconButton size="small" onClick={() => setGroupCount(g, g.count - 1)} disabled={loading || g.count <= 1} sx={{ p: 0.1, border: `1px solid ${C.chartGrid}` }}>−</IconButton>
                    <span style={{ fontWeight: 800, minWidth: 16, textAlign: 'center' }}>{g.count}</span>
                    <IconButton size="small" onClick={() => setGroupCount(g, g.count + 1)} disabled={loading || g.count >= 10} sx={{ p: 0.1, border: `1px solid ${C.chartGrid}` }}>+</IconButton>
                  </Box>
                  <span style={{ color: 'rgba(127,127,127,0.8)', fontSize: '0.72rem' }}>{g.expiry}</span>
                </Box>
              ))}
              {error && <Typography variant="caption" sx={{ color: C.warn }}>{error}</Typography>}
            </Box>

            <Typography variant="caption" color="text.secondary">
              Adjust strikes/lots to reshape the payoff — recomputed live, no order placed. <strong>Paper trade</strong> simulates the fills (risk-free, still monitored); <strong>Place LIVE</strong> routes all legs through your daily-loss / kill-switch gate (buy legs first), then the monitor manages it (TP {tpPct}% of max profit / SL {slPct}% of max loss).
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">Cancel</Button>
        {/* Synthetic symbols — persist this basket as a named pseudo-instrument in the
            symbol pane (no order placed). Hidden while the server feature flag is off
            (the save then returns a 403 and we show its message). */}
        <Button
          onClick={async () => {
            if (!card || !legs.length) return;
            setBusy(true);
            const r = await syntheticSymbolsService.create({
              underlyingSymbol, strategyId,
              legs: legs.map(l => ({ action: l.action, optionType: l.optionType, strike: l.strike, expiry: l.expiry })),
              netPremium: Number(card.netPremium) || 0,
            });
            setBusy(false);
            if (r.ok) {
              toast.success(`💾 Saved as synthetic symbol “${r.name}” — find it in the Synthetic pane (left).`, { duration: 7000, id: 'synth-save' });
              try { window.dispatchEvent(new Event('synthetics-changed')); } catch { /* noop */ }
            } else {
              toast.error(r.reason || 'Could not save as a synthetic symbol', { id: 'synth-save' });
            }
          }}
          disabled={busy || !disp || loading}
          variant="outlined"
          color="secondary"
        >
          💾 Save as symbol
        </Button>
        <Button onClick={() => place('PAPER')} disabled={busy || !disp || loading} variant="outlined" color="info">
          {busy ? 'Placing…' : '📝 Paper trade'}
        </Button>
        <Button onClick={() => setConfirmLive(true)} disabled={busy || !disp || loading} variant="contained" color="error">
          🟢 Place LIVE
        </Button>
      </DialogActions>

      {/* LIVE confirm — explicit sign-off with legs / qty / margin / max-loss. */}
      <Dialog open={confirmLive} onClose={() => { if (!busy) setConfirmLive(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>Place LIVE on exchange?</DialogTitle>
        <DialogContent>
          <Box sx={{ bgcolor: C.warnMuted, border: `1px solid ${C.warn}`, borderRadius: 1, p: 1, mb: 1.5 }}>
            <Typography variant="body2">This places <strong>real orders</strong> on {underlyingSymbol} via your broker (protective BUY legs first), subject to the kill-switch / daily-loss / margin gates.</Typography>
          </Box>
          {disp && (
            <>
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>{strategyLabel} · ×{lots} {lots === 1 ? 'lot' : 'lots'}</Typography>
              {legs.map((l, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                  {l.action === 'BUY' ? '＋' : '－'} {l.action} {l.optionType} {l.strike} <span style={{ opacity: 0.6 }}>{l.expiry}</span>
                </Typography>
              ))}
              <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                <Metric label="Margin required" value={fmtInr(Number(disp.marginEstimate || 0))} />
                <Metric label="Max profit" value={fmtInr(Number(disp.maxProfit || 0))} color={C.bull} />
                <Metric label="Max loss" value={fmtInr(Math.abs(Number(disp.maxLoss || 0)))} color={C.bear} />
              </Box>
              {spreadWarn && (
                <Box sx={{ mt: 1.5, bgcolor: C.warnMuted, border: `1px solid ${C.warn}`, borderRadius: 1, p: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block', color: C.warn, fontWeight: 700 }}>
                    ⚠ Wide bid/ask spread (up to {(worstSpreadPct * 100).toFixed(1)}%) — a market order may fill at a worse price.
                  </Typography>
                  <FormControlLabel sx={{ mt: 0.3 }} control={<Checkbox size="small" checked={spreadAck} onChange={e => setSpreadAck(e.target.checked)} />}
                    label={<Typography variant="caption">I understand — place anyway</Typography>} />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmLive(false)} disabled={busy} color="inherit">Cancel</Button>
          <Button onClick={() => place('LIVE')} disabled={busy || (spreadWarn && !spreadAck)} variant="contained" color="error" startIcon={busy ? <CircularProgress size={15} color="inherit" /> : undefined}>
            {busy ? 'Placing…' : 'Place LIVE'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default BasketReviewDialog;
